// Onay kutulu ağaçların (CheckTree — katalog ağacı, paket üye ağacı) saf görünüm mantığı:
// DR17-2 klavye sözleşmesi (↑/↓ Home/End satır, → aç / ilk çocuk, ← kapat / üst, Space işaretle) ve
// DR17-6 sanallaştırma penceresi — seçim/odak KİMLİK bazlı, odaklı satır pencere dışına kaysa da
// DOM'da kalır (ayrı tek satırlık dilim + doldurucular; kaydırma ofseti değişince odak kaybolmaz).

import type { VirtualWindow } from "./kpi-grid-virtual";

export interface TreeViewRow {
  id: string;
  parentId: string | null;
  hasChildren: boolean;
  expanded: boolean;
  /** onay kutusu var ve etkin (Space işaretler) */
  checkable: boolean;
}

export type TreeKeyAction =
  | { kind: "focus"; id: string }
  | { kind: "expand"; id: string; open: boolean }
  | { kind: "check"; id: string }
  | null;

/** Odak kimliği listede yoksa (süzgeç/kapanma sonrası) ilk satır. */
export function resolveFocusId(
  rows: readonly TreeViewRow[],
  focusId: string | null,
): string | null {
  if (rows.length === 0) return null;
  if (focusId !== null && rows.some((r) => r.id === focusId)) return focusId;
  return rows[0]!.id;
}

export function treeKeyAction(
  rows: readonly TreeViewRow[],
  focusId: string | null,
  key: string,
): TreeKeyAction {
  const id = resolveFocusId(rows, focusId);
  if (id === null) return null;
  const i = rows.findIndex((r) => r.id === id);
  const row = rows[i]!;
  switch (key) {
    case "ArrowDown":
      return i + 1 < rows.length ? { kind: "focus", id: rows[i + 1]!.id } : null;
    case "ArrowUp":
      return i > 0 ? { kind: "focus", id: rows[i - 1]!.id } : null;
    case "Home":
      return { kind: "focus", id: rows[0]!.id };
    case "End":
      return { kind: "focus", id: rows[rows.length - 1]!.id };
    case "ArrowRight": {
      if (!row.hasChildren) return null;
      if (!row.expanded) return { kind: "expand", id, open: true };
      const next = rows[i + 1];
      return next?.parentId === id && next ? { kind: "focus", id: next.id } : null;
    }
    case "ArrowLeft": {
      if (row.hasChildren && row.expanded) return { kind: "expand", id, open: false };
      if (row.parentId && rows.some((r) => r.id === row.parentId)) {
        return { kind: "focus", id: row.parentId };
      }
      return null;
    }
    case " ":
    case "Spacebar":
      return row.checkable ? { kind: "check", id } : null;
    default:
      return null;
  }
}

export type RenderSegment =
  | { kind: "pad"; height: number }
  | { kind: "rows"; start: number; end: number };

/**
 * Sanal pencerenin render dilimleri: pencere aralığı + (pencere dışındaysa) odaklı satır; aradaki
 * boşluklar sabit yükseklikli doldurucular. Pasif pencere → tek dilim (tüm satırlar, doldurucusuz).
 */
export function windowSegments(
  win: VirtualWindow,
  rowCount: number,
  focusIndex: number | null,
  rowHeight: number,
): RenderSegment[] {
  if (!win.active) return [{ kind: "rows", start: 0, end: rowCount }];
  const ranges: [number, number][] = [[win.start, win.end]];
  if (
    focusIndex !== null &&
    focusIndex >= 0 &&
    focusIndex < rowCount &&
    (focusIndex < win.start || focusIndex >= win.end)
  ) {
    ranges.push([focusIndex, focusIndex + 1]);
    ranges.sort((a, b) => a[0] - b[0]);
  }
  const out: RenderSegment[] = [];
  let cursor = 0;
  for (const [s, e] of ranges) {
    if (e <= s) continue;
    if (s > cursor) out.push({ kind: "pad", height: (s - cursor) * rowHeight });
    out.push({ kind: "rows", start: s, end: e });
    cursor = e;
  }
  if (rowCount > cursor) out.push({ kind: "pad", height: (rowCount - cursor) * rowHeight });
  return out;
}

/** Dilimlerde render edilen satır indeksleri (test + `aria-setsize` için). */
export function renderedIndices(segments: readonly RenderSegment[]): number[] {
  const out: number[] = [];
  for (const s of segments)
    if (s.kind === "rows") for (let i = s.start; i < s.end; i++) out.push(i);
  return out;
}

/**
 * `aria-setsize` / `aria-posinset` (TODO-UX-29/1): sanal pencerede DOM'da yalnız bir dilim vardır,
 * ekran okuyucu konumu DOM'dan çıkaramaz — kardeş grubu (aynı `parentId`) tüm satırlardan sayılır.
 */
export function ariaPositions(
  rows: readonly Pick<TreeViewRow, "parentId">[],
): { setSize: number; posInSet: number }[] {
  const total = new Map<string | null, number>();
  for (const r of rows) total.set(r.parentId, (total.get(r.parentId) ?? 0) + 1);
  const seen = new Map<string | null, number>();
  return rows.map((r) => {
    const pos = (seen.get(r.parentId) ?? 0) + 1;
    seen.set(r.parentId, pos);
    return { setSize: total.get(r.parentId)!, posInSet: pos };
  });
}
